import React, { FunctionComponent } from "react";
import styles from "./BookingWidget.module.scss";
import BookingWidgetBlockProps from "./BookingWidget.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import CollinsWidget from "./components/CollinsWidget";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getTextAlignment from "helpers/getTextAlignment";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const BookingWidget: FunctionComponent<BookingWidgetBlockProps> = (
  props
): JSX.Element => {
  const wrapperClasses = getWrapperClasses(props.settings);
  const wrapperStyles = useWrapperStyles(props.settings);
  const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

  const identifier = useAnchor(props.settings.anchorMenuLabel);
  const { splitClasses, splitStyles } = useSplitBackground(props);

  const headlineCaseClass = props.settings.headlineCase
    ? styles[`case_${props.settings.headlineCase}`]
    : undefined;

  if (props.settings?.textAlignment) {
    wrapperStyles["--text-alignment"] = getTextAlignment(
      props.settings?.textAlignment
    );
  }

  return (
    <section
      className={combineClasses(
        styles.wrapper,
        inViewClasses,
        ...wrapperClasses
      )}
      style={{ ...wrapperStyles, ...splitStyles }}
      id={identifier}
      ref={inViewElementRef}
    >
      <div className={combineClasses(styles.container, ...splitClasses)}>
        {/* CONTENT */}
        <div className={styles.content}>
          {/* Headline */}
          {props.content.headline ? (
            <DynamicHeading
              key={`head_${props.contentUdi}`}
              className={combineClasses(styles.headline, headlineCaseClass)}
              text={props.content.headline}
              headlineSize={props.settings.headlineSize}
              headlineWeight={props.settings.headlineWeight}
            />
          ) : null}

          {/* Text */}
          {props.content.text ? (
            <Paragraph
              key={`para_${props.contentUdi}`}
              className={styles.text}
              innerHTML={props.content.text}
            />
          ) : null}
        </div>

        {/* WIDGET */}
        <div className={styles.widget}>
          <CollinsWidget {...props} />
        </div>
      </div>

      {props.settings.backgroundMedia?.[0] &&
      props.settings.backgroundMedia.length > 0 ? (
        <BackgroundMedia
          key={props.contentUdi}
          {...props.settings.backgroundMedia[0]}
        />
      ) : null}
    </section>
  );
};

export default BookingWidget;
